/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { EmailRegex, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import { CheckoutModule, ErrorLocation } from '@msdyn365-commerce/global-state';
import classnames from 'classnames';
import { action, computed, reaction, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { focusOnCheckoutError } from '@msdyn365-commerce-modules/checkout';
import { ErrorComponent } from '@msdyn365-commerce-modules/checkout';

import { ICheckoutGuestProfileData } from './checkout-guest-profile.data';
import { ICheckoutGuestProfileProps } from './checkout-guest-profile.props.autogenerated';
import getAddContactInfo, { IAddContactInfo } from './components/get-add-contact-info';
import getShowContactInfo, { IShowContactInfo, PickupInfo } from './components/get-show-contact-info';
import { AttributeTextValue } from '@msdyn365-commerce/retail-proxy';

export * from './components/get-add-contact-info';
export * from './components/get-show-contact-info';

interface ICheckoutGuestProfileState {
    email: string;
    newsletter: boolean;
    pickupInfo: PickupInfo | null;
    pickupNameInvalid: boolean;
    hasCheckoutError?: boolean;
    errorMessage?: string;
}

export interface ICheckoutGuestProfileModuleProps extends ICheckoutGuestProfileProps<ICheckoutGuestProfileData>, IModuleStateProps {}

export interface ICheckoutGuestProfileViewProps extends ICheckoutGuestProfileProps<ICheckoutGuestProfileData>, ICheckoutGuestProfileState {
    moduleProps: IModuleProps;
    showContactInfo?: IShowContactInfo;
    addContactInfo?: IAddContactInfo;
    alert?: React.ReactNode;
    checkoutErrorRef?: React.RefObject<HTMLElement>;

    onChange?(email: string, newsletter: boolean, pickupInfo: PickupInfo): void;
    onEdit?(): void;
    onSubmit?(): void;
    onCancel?(): void;
}

/**
 *
 * CheckoutGuestProfile component.
 * @extends {React.PureComponent<ICheckoutGuestProfileProps<ICheckoutGuestProfileData>, ICheckoutGuestProfileState>}
 */
// @ts-expect-error
@withModuleState
@observer
class CheckoutGuestProfile extends React.Component<ICheckoutGuestProfileModuleProps, ICheckoutGuestProfileState> {
    public state: ICheckoutGuestProfileState = {
        newsletter: false,
        pickupInfo: null,
        pickupNameInvalid: false,
        email:
            (this.props.context &&
                this.props.context.request &&
                this.props.context.request.user &&
                this.props.context.request.user.isAuthenticated &&
                this.props.context.request.user.emailAddress) ||
            ''
    };

    private readonly inputRef: React.RefObject<HTMLInputElement> = React.createRef();
    private readonly pickupNameInputRef: React.RefObject<HTMLInputElement> = React.createRef();

    private readonly checkoutErrorRef: React.RefObject<HTMLElement> = React.createRef();

    @computed get isDataReady(): boolean {
        return (this.props.data.checkout.result && this.props.data.checkout.status) === 'SUCCESS';
    }

    @computed get hasPickup(): boolean {
        return !!this.props.data.checkout?.result?.checkoutCart.cart.CartLines?.find(sl => sl.DeliveryMode === 'Pick-up');
    }

    @computed get hasEmailError(): boolean {
        return this.props.moduleState.hasError && !this.state.hasCheckoutError;
    }

    public componentDidMount(): void {
        when(
            () => this.isDataReady,
            async () => {
                await this.init();
            }
        );

        reaction(
            () => this.props.data.checkout.result?.isExpressCheckoutApplied,
            async isExpressCheckoutApplied => {
                const emailAddressFromExpressCheckout = this.props.data.checkout.result?.emailAddressFromExpressCheckout ?? '';
                const isEmailFromExpressCheckoutDifferent =
                    !StringExtensions.isNullOrEmpty(emailAddressFromExpressCheckout) &&
                    emailAddressFromExpressCheckout !== this.state.email;

                if (
                    isExpressCheckoutApplied &&
                    (!this.props.data.checkout.result?.isExpressAddressApplied || isEmailFromExpressCheckoutDifferent)
                ) {
                    await this.submitEmail(emailAddressFromExpressCheckout, this.getNewsletter(), this.getPickupInfo());
                }
            }
        );

        if (this.props.data.checkout.result?.shouldEnableCheckoutErrorDisplayMessaging) {
            reaction(
                () => this.props.data.checkout.result?.checkoutError,
                checkoutError => {
                    if (checkoutError && checkoutError.errorLocation === ErrorLocation.CheckoutGuestProfile && checkoutError.errorMessage) {
                        this._setErrorMessage(checkoutError.errorMessage);
                    }
                }
            );

            reaction(
                () => this.props.data.checkout.result?.checkoutErrorFocus,
                checkoutErrorFocus => {
                    if (checkoutErrorFocus === CheckoutModule.CheckoutGuestProfile) {
                        void focusOnCheckoutError(this.checkoutErrorRef, this.props.context.actionContext);
                    }
                }
            );
        }
    }

    public shouldComponentUpdate(nextProps: ICheckoutGuestProfileModuleProps, nextState: ICheckoutGuestProfileState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const {
            config: { className },
            resources,
            moduleState: { isReady, hasExternalSubmitGroup }
        } = this.props;
        const { hasCheckoutError, errorMessage } = this.state;
        const email = this.props.data.checkout.result?.guestCheckoutEmail ?? '';

        const hasPickup = this.hasPickup;
        const pickupInfo = hasPickup ? this.getPickupInfo() : null;
        process.env.NODE_ENV === 'development' && console.log('checkout-guest-profile', 'render', hasPickup, pickupInfo);

        const viewProps: ICheckoutGuestProfileViewProps = {
            ...this.props,
            ...this.state,
            checkoutErrorRef: this.checkoutErrorRef,
            onChange: this.onChange,
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,

            moduleProps: {
                moduleProps: this.props,
                className: classnames('ms-checkout-guest-profile', className, isReady ? 'show' : 'add')
            },
            alert: hasCheckoutError && errorMessage && (
                <ErrorComponent {...{ title: resources.errorMessageTitle, message: errorMessage }} />
            ),
            showContactInfo: isReady
                ? getShowContactInfo({
                      canEdit: !hasExternalSubmitGroup,
                      email,
                      pickupInfo,
                      newsletter: this.state.newsletter,
                      resources,
                      onChange: this.onNewsletterChange,
                      onEdit: this.onEdit
                  })
                : undefined,
            addContactInfo: !isReady
                ? getAddContactInfo({
                      email: this.state.email,
                      newsletter: this.state.newsletter,
                      pickupInfo: hasPickup ? this.state.pickupInfo : null,
                      pickupNameInvalid: this.state.pickupNameInvalid,
                      hasError: this.hasEmailError,
                      resources,
                      inputRef: this.inputRef,
                      pickupNameInputRef: this.pickupNameInputRef,
                      onChange: this.onChange,
                      canSubmit: !hasExternalSubmitGroup,
                      onSubmit: this.onSubmit,
                      canCancel: !hasExternalSubmitGroup && !!email,
                      onCancel: this.onCancel
                  })
                : undefined
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private readonly init = async (): Promise<void> => {
        const checkoutState = this.props.data.checkout.result;
        const customerAccountInformation = this.props.data.accountInformation?.result;

        const hasInvoiceLine = checkoutState?.checkoutCart.hasInvoiceLine;
        let updateEmail: string = '';

        // if obo enabled and profile selected, we need to send the order status to obo user not store manager.
        if (Msdyn365.isOboRequest(this.props.context.request)) {
            updateEmail =
                customerAccountInformation && customerAccountInformation.Email !== undefined
                    ? customerAccountInformation.Email
                    : this.setErrorMsgOboUserEmailEmpty();
        } else {
            updateEmail = this.state.email;
        }

        const emailOnCart = checkoutState?.checkoutCart.cart.ReceiptEmail;
        const email = hasInvoiceLine
            ? this.props.context.request.user.emailAddress ?? ''
            : emailOnCart ??
              (checkoutState?.isExpressCheckoutApplied && !StringExtensions.isNullOrEmpty(checkoutState.emailAddressFromExpressCheckout)
                  ? checkoutState.emailAddressFromExpressCheckout
                  : undefined) ??
              updateEmail;

        const isEmailValid = this.isEmailValid(email);
        this.props.moduleState.init({
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,
            status: hasInvoiceLine ? 'disabled' : email && isEmailValid ? 'ready' : 'updating',
            hasError: !!email && !isEmailValid
        });

        if (email) {
            await this.updateGuestProfile(email);
        }

        this.setState({ newsletter: this.getNewsletter() });
        if (this.hasPickup) {
            const pickupInfo = this.getPickupInfo();
            this.setState({ pickupInfo, pickupNameInvalid: false });
        }

        await checkoutState?.updateHasGuestCheckoutEmail({ newHasGuestCheckoutEmail: false });
    };

    private readonly onChange = (rowEmail: string, newsletter: boolean, pickupInfo: PickupInfo | null): void => {
        process.env.NODE_ENV === 'development' && console.log('checkout-guest-profile', 'onChange', rowEmail, newsletter, pickupInfo);
        const email = (rowEmail || '').replace(new RegExp('[<>]', 'gi'), '');
        var emailChanged = email !== this.state.email;
        var pickupNameChanged = (pickupInfo?.name ?? '') !== (this.state.pickupInfo?.name ?? '');
        this.setState({ email, newsletter, pickupInfo });
        if (this.props.moduleState.hasError && emailChanged) {
            // Clear error during user updating the email
            this.props.moduleState.setHasError(false);
            this.setState({
                hasCheckoutError: false
            });
        }

        if (pickupNameChanged) {
            this.setState({
                pickupNameInvalid: false
            });
        }
    };

    private readonly onNewsletterChange = (newsletter: boolean): void => {
        void this.setNewsletter(newsletter).then(() => this.setState({ newsletter }));
    };

    private readonly setErrorMsgOboUserEmailEmpty = (): string => {
        // Clear prior error
        this.props.moduleState.setHasError(false);
        this.setState({
            pickupNameInvalid: false,
            hasCheckoutError: false
        });
        return '';
    };

    private readonly isEmailValid = (email: string): boolean => {
        return EmailRegex.defaultRegex.test(email);
    };

    private readonly updateGuestProfile = async (newGuestCheckoutEmail: string): Promise<void> => {
        if (this.props.data.checkout.result) {
            await this.props.data.checkout.result.updateGuestCheckoutEmail({ newGuestCheckoutEmail });
            this.setState({ email: newGuestCheckoutEmail });
        }
    };

    private readonly onEdit = (): void => {
        this.props.telemetry.information('Guest profile onEdit is called.');

        this.props.moduleState.onUpdating();
    };

    private readonly onSubmit = async (): Promise<void> => {
        this.props.telemetry.information('Guest profile onSubmit is called.');

        const { email, newsletter, pickupInfo } = this.state;
        await this.submitEmail(email, newsletter, pickupInfo);
    };

    /**
     * Submit the email address.
     * @param email - The email to be submitted.
     *
     */
    private readonly submitEmail = async (email: string, newsletter: boolean, pickupInfo: PickupInfo | null): Promise<void> => {
        const isValid = this.isEmailValid(email);
        const isPickupValid = !this.hasPickup || !!pickupInfo?.name;
        if (!isValid) {
            this.props.moduleState.setHasError(true);

            const input = this.inputRef.current?.focus && (this.inputRef.current as HTMLElement);
            input?.focus();
        } else if (!isPickupValid) {
            this.setState({ pickupNameInvalid: true });
            const input = this.pickupNameInputRef.current?.focus && (this.pickupNameInputRef.current as HTMLElement);
            input?.focus();
        } else {
            this.props.moduleState.onReady();
            await this.setNewsletter(newsletter);
            pickupInfo && (await this.setPickupInfo(pickupInfo));
            await this.updateGuestProfile(email);
        }
    };

    private readonly onCancel = (): void => {
        this.props.telemetry.information('Guest profile onCancel is called.');

        // Reset to the saved value
        if (!this.props.data.checkout.result) {
            return;
        }

        const email = this.props.data.checkout.result.guestCheckoutEmail;
        const pickupInfoReady = !this.hasPickup || !!this.state.pickupInfo?.name;
        if (!email || !pickupInfoReady) {
            // It has no saved email, set to status updating and request user to fill the email form
            this.props.moduleState.onUpdating();
        } else {
            // It has saved email, set to status ready
            this.setState({
                email,
                pickupInfo: this.getPickupInfo(),
                newsletter: this.getNewsletter()
            });
            this.props.moduleState.setHasError(false);
            this.setState({
                hasCheckoutError: false
            });
            this.props.moduleState.onReady();
        }
    };

    @action
    private readonly _setErrorMessage = (errorMessage: string): void => {
        this.props.telemetry.error(errorMessage);
        this.props.moduleState.setHasError(true);
        this.props.moduleState.onUpdating();
        this.setState({
            hasCheckoutError: true,
            errorMessage
        });
    };

    private getPickupInfo(): PickupInfo {
        const attr = this.getAttr('PickupInfo');
        if (attr?.TextValue) {
            try {
                return (JSON.parse(attr.TextValue) as PickupInfo) ?? {};
            } catch {
                console.log('Invalid pickup info');
            }
        }

        return {};
    }

    private getNewsletter(): boolean {
        return this.getAttr('OrderNewsletter')?.TextValue === 'Yes';
    }

    private getAttr(name: string): AttributeTextValue | undefined {
        const cart = this.props.data.checkout.result;
        let attrObj = cart?.attributeValues?.find(a => a.Name === name) as AttributeTextValue | undefined;

        if (!attrObj) {
            attrObj = cart?.checkoutCart.cart.AttributeValues?.find(a => a.Name === name) as AttributeTextValue | undefined;
        }

        return attrObj;
    }

    private async setNewsletter(newsletter: boolean) {
        const cart = this.props.data.checkout.result;
        if (cart) {
            const attrs = cart.attributeValues?.slice() ?? [];
            let heiNewsletterObj = attrs.find(a => a.Name === 'OrderNewsletter') as AttributeTextValue | undefined;

            if ((heiNewsletterObj?.TextValue === 'Yes') !== newsletter) {
                if (!heiNewsletterObj) {
                    if (!newsletter) {
                        return;
                    }

                    heiNewsletterObj = {
                        // @ts-ignore
                        '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                        Name: 'OrderNewsletter',
                        ExtensionProperties: []
                    };

                    attrs.push(heiNewsletterObj!);
                }

                heiNewsletterObj!.TextValue = newsletter ? 'Yes' : 'No';
                process.env.NODE_ENV === 'development' && console.log('checkout-guest-profile', 'setNewsletter', attrs);
                await this.props.data.checkout.result?.updateAttributeValues({ newAttributeValues: attrs });
                await this.props.data.checkout.result?.checkoutCart.updateAttributeValues({ newAttributeValues: attrs });
                this.setState({ newsletter: newsletter });
            }
        }
    }

    private async setPickupInfo(pickupInfo: PickupInfo) {
        const currentVal = this.getPickupInfo();
        if ((currentVal.name ?? '') === (pickupInfo.name ?? '') && (currentVal.notes ?? '') === (pickupInfo.notes ?? '')) {
            return;
        }

        const cart = this.props.data.checkout.result;
        if (cart) {
            const attrs = cart.attributeValues?.slice() ?? [];
            let attrObj = attrs.find(a => a.Name === 'PickupInfo') as AttributeTextValue | undefined;

            if (!attrObj) {
                attrObj = {
                    // @ts-ignore
                    '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                    Name: 'PickupInfo',
                    ExtensionProperties: []
                };

                attrs.push(attrObj!);
            }

            attrObj!.TextValue = JSON.stringify(pickupInfo);
            process.env.NODE_ENV === 'development' && console.log('checkout-guest-profile', 'setPickupInfo', attrs);
            await this.props.data.checkout.result?.updateAttributeValues({ newAttributeValues: attrs });
            await this.props.data.checkout.result?.checkoutCart.updateAttributeValues({ newAttributeValues: attrs });
            this.setState({ pickupInfo });
        }
    }
}

export default CheckoutGuestProfile;
